import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Table } from '@/components/ui/tables/Table';
import type { TableColumn } from '@/components/ui/tables/Table';
import { colors } from '@/assets/styles/colors';
import { MonitoringColumnHeader } from '@/components/features/attendance/monitoring/MonitoringColumnHeader';
import type { LC_MonitoringFiltersIn, MonitoringJustified } from '@/api/domains/attendance/monitoring';
import {
  useMonitoringJustified,
  MONITORING_JUSTIFIED_DEFAULT_SORT,
} from '@/hooks/attendance/monitoring/useMonitoringJustified';
import { MONITORING_TABLE_MIN_WIDTH } from '@/components/features/attendance/monitoring/monitoringTable.constants';


export interface MonitoringJustifiedTableProps {
  appliedFilters: LC_MonitoringFiltersIn | null;
  /** Token de refresco de la pantalla; cada cambio reconsulta. */
  refreshToken?: number;
}

export const MonitoringJustifiedTable: React.FC<MonitoringJustifiedTableProps> = ({
  appliedFilters,
  refreshToken,
}) => {
  const { t: translate } = useTranslation();

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    hasLoadedOnce,
    onSearch,
    onSortChange,
  } = useMonitoringJustified(appliedFilters, refreshToken);

  const isInitialLoading = !hasLoadedOnce;
  const isRefreshing = loading && hasLoadedOnce;

  const columns = useMemo<TableColumn<MonitoringJustified>[]>(
    () => [
      { key: 'rut', header: translate('attendance:monitoring.justified.columns.rut'), sortable: true },
      {
        key: 'fullName',
        header: translate('attendance:monitoring.justified.columns.collaborator'),
        sortable: true,
      },
      {
        key: 'statusCode',
        header: translate('attendance:monitoring.justified.columns.status'),
        sortable: true,
        render: (row) =>
          translate(`attendance:monitoring.justified.status.${row.statusCode}`, {
            defaultValue: translate('attendance:monitoring.justified.statusUnknown'),
          }),
      },
    ],
    [translate],
  );

  return (
    <div className="flex flex-col gap-3" data-testid="monitoring-justified">
      <MonitoringColumnHeader
        icon="userExclamation"
        iconColor={colors.feedback.alert300}
        title={translate('attendance:monitoring.justified.title')}
        isRefreshing={isRefreshing}
        refreshingTestId="monitoring-justified-refreshing"
      />
      <div
        className={`flex h-full flex-1 transition-opacity duration-200 ${isRefreshing ? 'pointer-events-none opacity-60' : ''}`}
        aria-busy={isRefreshing}
      >
        <Table<MonitoringJustified>
          className="w-full flex-1"
          minTableWidth={MONITORING_TABLE_MIN_WIDTH}
          columns={columns}
          data={rows}
          totalRows={total}
          isLoading={isInitialLoading}
          searchable
          searchPlaceholder={translate('attendance:monitoring.justified.searchPlaceholder')}
          onSearch={onSearch}
          serverSideSort
          initialSort={MONITORING_JUSTIFIED_DEFAULT_SORT}
          onSortChange={onSortChange}
          getRowId={(row) => row.rut}
          hideTotalRows={total === 0}
          pagination={
            total > 0
              ? { page, pageSize, total, onChange: setPage, onPageSizeChange: setPageSize }
              : undefined
          }
          data-testid="monitoring-justified-table"
        />
      </div>
    </div>
  );
};

export default MonitoringJustifiedTable;
